import L from 'leaflet';
import type { Feature, FeatureCollection, Geometry, GeoJsonProperties } from 'geojson';

export type AnyFeature = Feature<Geometry, GeoJsonProperties>;

export function getBoundsFromFeatures(features: AnyFeature[]): L.LatLngBounds | null {
  if (!features.length) return null;
  const fc: FeatureCollection = { type: 'FeatureCollection', features };
  const layer = L.geoJSON(fc);
  const b = layer.getBounds();
  return b.isValid() ? b : null;
}

export type AutoZoomBranch = 'single-plot' | 'single-zone' | 'fit-all' | 'fallback';

export interface AutoZoomOptions {
  /** tight zoom padding for single plot */
  singlePlotPadding?: L.PointTuple;
  singlePlotMaxZoom?: number;
  /** medium zoom padding for single zone */
  singleZonePadding?: L.PointTuple;
  singleZoneMaxZoom?: number;
  /** padding for multi-feature fit */
  fitAllPadding?: L.PointTuple;
  /** animation options shared across branches */
  duration?: number;
  easeLinearity?: number;
}

export function runAutoZoom(
  map: L.Map,
  zones: AnyFeature[],
  plots: AnyFeature[],
  fallbackBounds: L.LatLngBoundsExpression,
  opts: AutoZoomOptions = {},
): AutoZoomBranch {
  const {
    singlePlotPadding = [10, 10],
    singlePlotMaxZoom = 17,
    singleZonePadding = [20, 20],
    singleZoneMaxZoom = 16,
    fitAllPadding = [40, 40],
    duration = 1.5,
    easeLinearity = 0.2,
  } = opts;

  const zonesCount = zones.length;
  const plotsCount = plots.length;

  // Priority: if exactly one zone, zoom to it regardless of plots
  if (zonesCount === 1) {
    const b = getBoundsFromFeatures([zones[0]]);
    if (b) {
      map.invalidateSize();
      map.flyToBounds(b, {
        padding: singleZonePadding,
        maxZoom: singleZoneMaxZoom,
        duration,
        easeLinearity,
      });
      return 'single-zone';
    }
  }

  // Otherwise: if exactly one plot, zoom to that plot
  if (plotsCount === 1) {
    const b = getBoundsFromFeatures([plots[0]]);
    if (b) {
      map.invalidateSize();
      map.flyToBounds(b, {
        padding: singlePlotPadding,
        maxZoom: singlePlotMaxZoom,
        duration,
        easeLinearity,
      });
      return 'single-plot';
    }
  }

  // Case 3: multiple features
  const all = [...zones, ...plots];
  const b = getBoundsFromFeatures(all);
  if (b) {
    map.invalidateSize();
    map.flyToBounds(b, {
      padding: fitAllPadding,
      duration,
      easeLinearity,
    });
    return 'fit-all';
  }

  // Fallback if bounds invalid
  map.invalidateSize();
  map.fitBounds(fallbackBounds, { padding: [24, 24] });
  return 'fallback';
}
